import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Box,
  Button,
  CardActions,
  CardContent,
  TextField,
  Typography,
  CircularProgress,
  Container,
  Stack,
  Paper,
  InputAdornment,
} from "@mui/material";
import { useSnackbar } from "notistack";
import sh from "bundles/common/utils/sh";
import Crane from "icons/crane.svg";
import Logo from "icons/new-logo.svg";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";

function InternalLoginPage() {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const authenticity_token = useSelector((state) => state.authenticity_token);

  const [values, setValues] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  
  const handleInputValue = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    sh.post("/internal/sign_in", {
      authenticity_token,
      internal_user: {
        email: values.email,
        password: values.password,
      },
    })
      .then(() => {
        window.location.href = "/internal/dashboard";
      })
      .catch((err) => {
        setLoading(false);
        if (err.response?.status === 401) {
          enqueueSnackbar(
            intl.formatMessage({ id: "devise.login.unauthorized" }),
            { variant: "error" }
          );
        } else {
          enqueueSnackbar("Login failed.", { variant: "error" });
        }
      });
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "calc(0.75rem - 2px)",
      backgroundColor: "hsl(140 10% 95%)",
      fontSize: 15,
      fontFamily: "Segoe UI Regular, sans-serif, system-ui",
      color: "hsl(150 30% 12%)",
      "& fieldset": {
        borderColor: "transparent",
      },
      "&:hover fieldset": {
        borderColor: "hsl(140 8% 82%)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "hsl(140 8% 82%)",
        borderWidth: 1,
      },
    },
    "& .MuiInputLabel-root": {
      fontSize: 14,
      color: "hsl(150 10% 46%)",
      fontFamily: "Segoe UI Regular, sans-serif, system-ui",
      "&.Mui-focused": {
        color: "hsl(150 38% 17%)",
      },
    },
  };
  
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
      
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
      
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
      
    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
    };
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "hsl(150 38% 17%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        component="img"
        src={Crane}
        alt=""
        sx={{
          position: "absolute",
          right: { xs: "-50%", md: "2%" },
          top: "50%",
          transform: "translateY(-60%)",
          opacity: 0.06,
          maxHeight: "90vh",
          pointerEvents: "none",
          flex: 1
        }}
      />

      <Container
        maxWidth="sm"
        sx={{
          position: "relative",
          zIndex: 1,
          transform: { xs: "translateY(-24px)", md: "translateY(-12px)" },
        }}
      >
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Box
            component="img"
            src={Logo}
            alt="Squarehub"
            sx={{
              height: 48,
              mb: 2,
              filter: "brightness(0) invert(1)",
            }}
          />
        </Box>

        <Paper
          elevation={0}
          sx={{
            borderRadius: "0.75rem",
            overflow: "hidden",
            border: "1px solid hsl(140 8% 88%)",
          }}
        >
          <form onSubmit={handleSubmit}>
            {/* Card Header */}
            <Box
              sx={{
                backgroundColor: "#fff",
                px: 4,
                pt: 4,
                pb: 3,
                textAlign: "center",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: "hsl(150 30% 12%)",
                  fontWeight: 600,
                  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                  fontSize: 24,
                  mb: 1,
                }}
              >
                <FormattedMessage id="signin" values={{ b: (chunks) => <b>{chunks}</b> }} />
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "hsl(150 10% 46%)",
                  fontSize: 14,
                  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                }}
              >
                <FormattedMessage id="devise.login.internal_access" />
              </Typography>
            </Box>

            {/* Card Content */}
            <CardContent sx={{ px: 4, py: 3 }}>
              <Stack spacing={2.5}>
                <TextField
                  fullWidth
                  required
                  type="email"
                  variant="outlined"
                  onChange={handleInputValue}
                  name="email"
                  size="medium"
                  label={<FormattedMessage id="devise.signup.email" />}
                  sx={inputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: "hsl(150 38% 17%)", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  required
                  type="password"
                  variant="outlined"
                  onChange={handleInputValue}
                  name="password"
                  size="medium"
                  label={<FormattedMessage id="devise.signup.password" />}
                  sx={inputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: "hsl(150 38% 17%)", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
            </CardContent>

            {/* Card Actions */}
            <CardActions sx={{ px: 4, pb: 4, pt: 1 }}>
              <Button
                fullWidth
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                disableRipple
                disableElevation
                sx={{
                  backgroundColor: "hsl(150 38% 17%)",
                  color: "hsl(80 97% 72%)",
                  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                  borderRadius: "0.75rem",
                  textTransform: "none",
                  py: 1.5,
                  fontSize: 16,
                  transition: "opacity 0.15s ease",
                  "&:hover": {
                    backgroundColor: "hsl(150 38% 17%)",
                    opacity: 0.9,
                  },
                  "&:disabled": {
                    opacity: 0.4,
                    backgroundColor: "hsl(150 38% 17%)",
                    color: "hsl(80 97% 72%)",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: "hsl(80 97% 72%)" }} />
                ) : (
                  <FormattedMessage id="signin" />
                )}
              </Button>
            </CardActions>
          </form>
        </Paper>

        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "center",
            mt: 4,
            color: "hsl(140 10% 60%)",
            fontSize: 12,
          }}
        >
          © {new Date().getFullYear()} Squarehub. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
}

export default InternalLoginPage;